import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ChevronLeft, Loader2, Bike, Store, Banknote, ShoppingBag, CreditCard, Gift, Clock, X } from "lucide-react";
import api, { money, apiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { dayWindow, windowLabel } from "@/lib/hours";
import { findNearestOpenBranch } from "@/lib/branches";
import BranchSelect from "@/components/BranchSelect";
import { MapPin } from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "@/lib/seo";

const STEPS = ["Your Details", "Delivery", "Payment", "Review"];

export default function Checkout() {
  const { items, totals, orderType, setOrderType, postcode, setPostcode, promoCode, setPromoCode, note, clearCart, payload, selectedBranch, setSelectedBranch, branchStatus, scheduleIntent, setScheduleIntent, redeemPoints, setRedeemPoints } = useCart();
  const { user, login, register, refresh } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  useSeo({
    title: "Checkout | Metros Fried Chicken Epsom",
    description: "Complete your Metros order for fried chicken takeaway or food delivery in Epsom, Surrey.",
    path: "/checkout",
    noindex: true,
  });
  const [branchGuard, setBranchGuard] = useState(false);
  const branchBtnRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [saveDetails, setSaveDetails] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const errBorder = (k) => errors[k] ? "border-metros-red ring-1 ring-metros-red/40" : "border-gray-200 focus:ring-2 focus:ring-metros-red";
  const clearErr = (k) => setErrors((p) => { if (!p[k]) return p; const n = { ...p }; delete n[k]; return n; });
  const [switching, setSwitching] = useState(false);

  const switchToNearestOpen = async () => {
    setSwitching(true);
    const b = await findNearestOpenBranch({ postcode: address.postcode || postcode, excludeId: selectedBranch?.id });
    setSwitching(false);
    if (b) { setSelectedBranch({ id: b.id, name: b.name }); toast.success(`Switched to ${b.name.replace("METROS — ", "")}`); }
    else toast.error("No open branches nearby right now.");
  };
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [customer, setCustomer] = useState({ name: "", phone: "", email: "" });
  const [address, setAddress] = useState({ line1: "", line2: "", postcode: "" });
  const [deliveryInstructions, setDeliveryInstructions] = useState("");
  const [collectionTime, setCollectionTime] = useState("asap");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [pcStatus, setPcStatus] = useState(null);
  const [authForm, setAuthForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [authBusy, setAuthBusy] = useState(false);
  const [authErr, setAuthErr] = useState("");
  const [showAuth, setShowAuth] = useState(false);
  const [authView, setAuthView] = useState("choices"); // choices | signin | create

  const setAF = (k) => (e) => setAuthForm({ ...authForm, [k]: e.target.value });
  const doSignIn = async () => {
    setAuthBusy(true); setAuthErr("");
    try { await login(authForm.email, authForm.password); toast.success("Signed in"); await submitOrder(); }
    catch (e) { setAuthErr(apiError(e)); } finally { setAuthBusy(false); }
  };
  const doCreate = async () => {
    setAuthBusy(true); setAuthErr("");
    try { await register({ name: authForm.name, email: authForm.email, phone: authForm.phone, password: authForm.password }); toast.success("Account created"); await submitOrder(); }
    catch (e) { setAuthErr(apiError(e)); } finally { setAuthBusy(false); }
  };
  const loginWithGoogleCheckout = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/checkout";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const [placedOrderNumber, setPlacedOrderNumber] = useState(null);
  const pcTouched = useRef(false); // true once the customer manually edits / picks a saved address
  const [outsideInfo, setOutsideInfo] = useState(null); // { other: {id,name,distance} | null } — nearby branch that can deliver

  // Keep the delivery postcode in sync with the postcode the customer entered to find the
  // branch, until they manually edit it or select a saved address.
  useEffect(() => {
    if (postcode && !pcTouched.current) {
      setAddress((a) => (a.postcode === postcode ? a : { ...a, postcode }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postcode]);

  const isClosedNow = branchStatus?.is_open === false && collectionTime === "asap";

  const checkPostcode = async () => {
    if (!address.postcode) { setPcStatus(null); return; }
    if (!selectedBranch) { setBranchGuard(true); toast.error("Please select a branch to continue"); return; }
    try { const { data } = await api.get(`/delivery/check?postcode=${encodeURIComponent(address.postcode)}${selectedBranch ? `&branch_id=${encodeURIComponent(selectedBranch.id)}` : ""}`); setPcStatus(data); }
    catch { setPcStatus(null); }
  };

  useEffect(() => {
    // Refresh the signed-in user so the reward-points balance is current
    // (avoids the redeem option hiding due to a stale balance from page load).
    if (user) refresh?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      setCustomer({ name: user.name || "", phone: user.phone || "", email: user.email || "" });
      api.get("/account/addresses").then(({ data }) => setAddresses(data)).catch(() => {});
    }
  }, [user]);

  useEffect(() => { if (items.length === 0 && !placedOrderNumber) navigate("/menu"); }, [items, navigate, placedOrderNumber]);

  const pm = settings?.payment_methods || { cash: true };
  const delivW = dayWindow(settings, "delivery");
  const collW = dayWindow(settings, "collection");

  // Reward points redemption (backend caps to order value; we cap the UI similarly)
  const rw = settings?.rewards || {};
  const rwThreshold = Number(rw.reward_threshold || 0);
  const rwValue = Number(rw.reward_value || 0);
  const rwBalance = Number(user?.loyalty_points || 0);
  const rwRemaining = totals ? (Number(totals.total) + Number(totals.points_discount || 0)) : 0; // order value before points
  const rwEligible = !!user && rw.enabled && rwThreshold > 0 && rwValue > 0 && rwBalance >= rwThreshold;
  const rwMax = rwEligible
    ? Math.min(Math.floor(rwBalance / rwThreshold), Math.floor(rwRemaining / rwValue)) * rwThreshold
    : 0;
  // On card orders, cap redemption so the charged total never falls below Stripe's £0.30 minimum.
  const CARD_MIN = 0.30;
  const rwMaxCard = rwEligible && rwValue > 0
    ? Math.max(0, Math.floor((rwRemaining - CARD_MIN) / rwValue)) * rwThreshold
    : 0;
  const rwEffMax = paymentMethod === "card" ? Math.min(rwMax, rwMaxCard) : rwMax;

  // Keep requested redemption within bounds if the order/balance/payment method changes.
  useEffect(() => {
    if (redeemPoints > rwEffMax) setRedeemPoints(rwEffMax);
  }, [rwEffMax]); // eslint-disable-line

  useEffect(() => {
    const w = orderType === "delivery" ? delivW : collW;
    if (scheduleIntent) { setCollectionTime(w.open || "12:00"); setScheduleIntent(false); return; }
    if (w && !w.openNow && collectionTime === "asap") setCollectionTime(w.open || "12:00");
  }, [orderType, delivW.openNow, collW.openNow]); // eslint-disable-line

  useEffect(() => {
    if (pm.card && !pm.cash) setPaymentMethod("card");
  }, [pm.card, pm.cash]);

  const rewardToastShown = useRef(false);
  useEffect(() => {
    if (user && totals?.reward_order && Number(totals?.reward_discount) > 0 && !rewardToastShown.current) {
      rewardToastShown.current = true;
      toast.success(`🎉 ${totals.order_reward_percent}% loyalty reward unlocked — you saved ${money(totals.reward_discount)}!`, { duration: 6000 });
    }
    if (!totals?.reward_order) rewardToastShown.current = false;
  }, [totals?.reward_order, totals?.reward_discount]); // eslint-disable-line

  const back = () => setStep((s) => Math.max(0, s - 1));

  const next = () => {
    const e = {};
    if (step === 0) {
      if (!customer.name.trim()) e.name = "Please enter your name";
      if (!customer.phone.trim()) e.phone = "Please enter your phone number";
      else if (customer.phone.replace(/\D/g, "").length < 7) e.phone = "Please enter a valid phone number";
      if (customer.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email.trim())) e.email = "Please enter a valid email address";
    }
    if (step === 1 && orderType === "delivery") {
      if (!address.line1.trim()) e.line1 = "Please enter your address";
      if (!address.postcode.trim()) e.postcode = "Please enter your postcode";
    }
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("Please fix the highlighted details");
      const map = { name: "co-name", phone: "co-phone", email: "co-email", line1: "co-addr1", postcode: "co-postcode" };
      const first = ["name", "phone", "email", "line1", "postcode"].find((k) => e[k]);
      if (first) setTimeout(() => { const el = document.querySelector(`[data-testid="${map[first]}"]`); if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus(); } }, 60);
      return;
    }
    setStep((s) => Math.min(3, s + 1));
  };

  const outsideDelivery = orderType === "delivery" && pcStatus?.error === "outside";

  // When outside the current branch's radius, look for another OPEN branch that can deliver here.
  useEffect(() => {
    if (!(outsideDelivery && address.postcode)) { setOutsideInfo(null); return; }
    let active = true;
    api.get(`/branch/for-postcode?postcode=${encodeURIComponent(address.postcode)}`).then(({ data }) => {
      if (!active) return;
      const others = (data.branches || [])
        .filter((b) => b.delivers && b.is_open && b.id !== selectedBranch?.id)
        .sort((a, b) => (a.distance ?? 9999) - (b.distance ?? 9999));
      setOutsideInfo({ other: others[0] || null });
    }).catch(() => { if (active) setOutsideInfo(null); });
    return () => { active = false; };
  }, [outsideDelivery, address.postcode, selectedBranch]);

  const submitOrder = async () => {
    setShowAuth(false);
    if (outsideDelivery) { toast.error("Please select collection."); return; }
    if (!selectedBranch) { setBranchGuard(true); toast.error("Please select a branch before ordering"); return; }
    setBusy(true);
    try {
      if (paymentMethod === "card" && totals && Number(totals.total) < 0.30) {
        toast.error(`Card payments need at least £0.30. Your total after rewards is ${money(totals.total)} — please pay by cash or redeem fewer points.`);
        setBusy(false);
        return;
      }
      const body = {
        ...payload(), customer,
        address: orderType === "delivery" ? address : null,
        delivery_instructions: deliveryInstructions,
        collection_time: collectionTime, note, payment_method: paymentMethod,
        postcode: orderType === "delivery" ? address.postcode : "",
      };
      const { data } = await api.post("/orders", body);
      setPlacedOrderNumber(data.order_number);
      if (user && saveDetails) {
        try {
          await api.put("/account/profile", { name: customer.name, phone: customer.phone });
          if (orderType === "delivery" && address.line1 && address.postcode) {
            await api.post("/account/addresses", { label: "Home", line1: address.line1, line2: address.line2, postcode: address.postcode, instructions: deliveryInstructions });
          }
        } catch (e) { console.error("Saving customer details failed (non-blocking):", e); }
      }
      if (paymentMethod === "card") {
        // Card orders are only confirmed & kept once payment succeeds (see PaymentResult). Cart stays until then.
        const { data: pay } = await api.post("/payments/checkout", { order_number: data.order_number, origin_url: window.location.origin });
        window.location.href = pay.checkout_url;
        return;
      }
      try { localStorage.setItem("metros_last_order", data.order_number); } catch (e) { console.warn("Could not persist last order number:", e); }
      navigate(`/order/${data.order_number}`);
      clearCart();
      toast.success("Order placed!");
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  const placeOrder = () => {
    if (!selectedBranch) {
      setBranchGuard(true);
      branchBtnRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      toast.error("Please select a branch to place your order");
      return;
    }
    if (outsideDelivery) { toast.error("Please select collection."); return; }
    if (!user) { setAuthErr(""); setAuthView("choices"); setShowAuth(true); return; }
    submitOrder();
  };

  useEffect(() => {
    if (orderType === "delivery" && address.postcode) setPostcode(address.postcode);
  }, [address.postcode, orderType, setPostcode]);

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground mb-4"><ChevronLeft size={16} /> Back</button>
      <h1 className="font-heading font-extrabold text-3xl mb-5">Checkout</h1>

      {/* Fulfilling branch card — shows open hours today + estimated time; tap to change (delivery & collection) */}
      <div data-testid="checkout-branch-card"
        className={`rounded-2xl px-4 py-3.5 mb-5 border-2 transition-all ${selectedBranch ? "border-metros-red/30 bg-metros-red/5" : "border-amber-400 bg-amber-100 ring-2 ring-amber-300/70 shadow-md animate-pulse"}`}>
        <div className="flex items-center gap-2">
          <MapPin size={18} className={selectedBranch ? "text-metros-red shrink-0" : "text-amber-600 shrink-0"} />
          <p className="flex-1 min-w-0 text-sm font-semibold text-metros-black">
            {selectedBranch
              ? <>{orderType === "delivery" ? "Delivered by " : "Collect from "}<b>{selectedBranch.name.replace("METROS — ", "")}</b></>
              : "Please select a branch to continue"}
          </p>
          <button data-testid="checkout-change-branch" onClick={() => setBranchGuard(true)}
            className="text-metros-red font-bold text-sm underline shrink-0">{selectedBranch ? "Change" : "Select"}</button>
        </div>
        {selectedBranch && (
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs pl-6" data-testid="checkout-branch-info">
            {branchStatus?.is_open ? (
              <span className="inline-flex items-center gap-1.5 font-semibold text-green-700"><span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Open today{branchStatus.opens_at ? ` · ${branchStatus.opens_at}–${branchStatus.closes_at}` : ""}</span>
            ) : (
              <span className="inline-flex items-center gap-1.5 font-semibold text-metros-red"><span className="w-1.5 h-1.5 rounded-full bg-red-500" /> Closed now{branchStatus?.opens_at ? ` · opens ${branchStatus.opens_at}` : ""}</span>
            )}
            {orderType === "delivery"
              ? (settings?.delivery?.estimated_time && <span className="inline-flex items-center gap-1.5 text-metros-black" data-testid="checkout-eta"><Clock size={12} className="text-metros-red" /> Est. delivery {settings.delivery.estimated_time}</span>)
              : (settings?.collection?.prep_time && <span className="inline-flex items-center gap-1.5 text-metros-black" data-testid="checkout-prep"><Clock size={12} className="text-metros-red" /> Ready in {settings.collection.prep_time}</span>)}
          </div>
        )}
      </div>

      {/* Closed-branch guard banner — offer nearest open branch automatically */}
      {branchStatus?.is_open === false && (
        <div data-testid="checkout-branch-closed" className="w-full rounded-2xl px-4 py-3.5 mb-5 text-sm font-semibold bg-red-50 border-2 border-red-200 text-metros-red flex flex-wrap items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
          {isClosedNow ? (
            <>
              <span className="flex-1">{selectedBranch?.name} is closed right now{branchStatus?.opens_at ? ` · opens at ${branchStatus.opens_at}` : ""}.</span>
              <button data-testid="checkout-switch-open" onClick={switchToNearestOpen} disabled={switching} className="underline font-bold flex items-center gap-1 disabled:opacity-60">{switching && <Loader2 className="animate-spin" size={13} />}Switch to nearest open branch</button>
              <span className="text-xs text-metros-red/70">or schedule for later below</span>
            </>
          ) : (
            <span className="flex-1">{selectedBranch?.name} is closed right now{branchStatus?.opens_at ? ` · opens at ${branchStatus.opens_at}` : ""} — your order will be scheduled for later.</span>
          )}
        </div>
      )}

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6">
        {STEPS.map((label, i) => (
          <div key={label} className="flex-1 flex items-center gap-2">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i < step ? "bg-metros-black text-white" : i === step ? "bg-metros-red text-white" : "bg-gray-100 text-gray-400"}`}>
              {i < step ? <Check size={15} /> : i + 1}
            </div>
            {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 ${i < step ? "bg-metros-black" : "bg-gray-100"}`} />}
          </div>
        ))}
      </div>

      <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
        {step === 0 && (
          <div className="space-y-4" data-testid="step-details">
            <h2 className="font-heading font-bold text-xl">Your Details</h2>
            {user && <p className="text-sm text-green-600 font-semibold" data-testid="signed-in-note">✓ Signed in as {user.name}</p>}
            {!user && <p className="text-sm text-muted-foreground">Enter your contact details. You can sign in, use Google, or create an account when you place your order — or just continue as a guest.</p>}
            <input data-testid="co-name" placeholder="Full name" value={customer.name} onChange={(e) => { setCustomer({ ...customer, name: e.target.value }); clearErr("name"); }} className={`w-full rounded-xl border px-4 py-3 focus:outline-none ${errBorder("name")}`} />
            {errors.name && <p data-testid="err-name" className="text-xs text-metros-red -mt-2">{errors.name}</p>}
            <input data-testid="co-phone" placeholder="Phone number" value={customer.phone} onChange={(e) => { setCustomer({ ...customer, phone: e.target.value }); clearErr("phone"); }} className={`w-full rounded-xl border px-4 py-3 focus:outline-none ${errBorder("phone")}`} />
            {errors.phone && <p data-testid="err-phone" className="text-xs text-metros-red -mt-2">{errors.phone}</p>}
            <input data-testid="co-email" placeholder="Email (for order updates)" value={customer.email} onChange={(e) => { setCustomer({ ...customer, email: e.target.value }); clearErr("email"); }} className={`w-full rounded-xl border px-4 py-3 focus:outline-none ${errBorder("email")}`} />
            {errors.email && <p data-testid="err-email" className="text-xs text-metros-red -mt-2">{errors.email}</p>}
            {user && (
              <label data-testid="save-details" className="flex items-center gap-2 text-sm text-gray-600 pt-1 cursor-pointer">
                <input type="checkbox" checked={saveDetails} onChange={(e) => setSaveDetails(e.target.checked)} className="w-4 h-4 accent-metros-red" />
                Save these details for next time
              </label>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4" data-testid="step-fulfilment">
            <div className="grid grid-cols-2 gap-2 bg-gray-100 p-1 rounded-full">
              {[["delivery", Bike, "Delivery"], ["collection", Store, "Collection"]].map(([v, Icon, label]) => (
                <button key={v} data-testid={`co-type-${v}`} onClick={() => setOrderType(v)} className={`flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-semibold ${orderType === v ? "bg-metros-black text-white" : "text-gray-600"}`}>
                  <Icon size={16} /> {label}
                </button>
              ))}
            </div>
            {orderType === "delivery" ? (
              <>
                {addresses.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {addresses.map((a) => (
                      <button key={a.id} onClick={() => { pcTouched.current = true; setAddress({ line1: a.line1, line2: a.line2, postcode: a.postcode }); }} className="text-xs border border-gray-200 rounded-full px-3 py-1.5 hover:border-metros-red">{a.label}: {a.postcode}</button>
                    ))}
                  </div>
                )}
                <input data-testid="co-addr1" placeholder="Address line 1" value={address.line1} onChange={(e) => { setAddress({ ...address, line1: e.target.value }); clearErr("line1"); }} className={`w-full rounded-xl border px-4 py-3 focus:outline-none ${errBorder("line1")}`} />
                {errors.line1 && <p data-testid="err-line1" className="text-xs text-metros-red -mt-2">{errors.line1}</p>}
                <input data-testid="co-addr2" placeholder="Address line 2 (optional)" value={address.line2} onChange={(e) => setAddress({ ...address, line2: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:ring-2 focus:ring-metros-red focus:outline-none" />
                <div className="flex gap-2">
                  <input data-testid="co-postcode" placeholder="Postcode (e.g. KT19 9UR)" value={address.postcode} onBlur={checkPostcode} onChange={(e) => { pcTouched.current = true; setAddress({ ...address, postcode: e.target.value }); setPcStatus(null); clearErr("postcode"); }} className={`flex-1 rounded-xl border px-4 py-3 uppercase focus:outline-none ${errBorder("postcode")}`} />
                  <button type="button" data-testid="check-postcode" onClick={checkPostcode} className="bg-metros-black text-white rounded-xl px-4 text-sm font-semibold">Check</button>
                </div>
                {errors.postcode && <p data-testid="err-postcode" className="text-xs text-metros-red -mt-2">{errors.postcode}</p>}
                {pcStatus && (
                  <p data-testid="postcode-status" className={`text-sm font-semibold ${pcStatus.ok ? "text-green-600" : "text-metros-red"}`}>
                    {pcStatus.ok ? "✓ Delivery available." : (pcStatus.error === "outside" ? "Outside our delivery area — collection only." : `✕ ${pcStatus.message}`)}
                  </p>
                )}
                <input data-testid="co-instructions" placeholder={settings?.delivery?.instructions_hint || "Delivery instructions"} value={deliveryInstructions} onChange={(e) => setDeliveryInstructions(e.target.value)} className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:ring-2 focus:ring-metros-red focus:outline-none" />
                <p className="text-xs text-muted-foreground">We deliver within {settings?.delivery?.radius_miles} miles. Est. {settings?.delivery?.estimated_time}.</p>
                <div className="space-y-2 pt-1" data-testid="delivery-schedule">
                  <p className="text-sm font-semibold">Delivery time — today {windowLabel(delivW)}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button data-testid="dl-time-asap" disabled={!delivW.openNow} onClick={() => setCollectionTime("asap")} className={`py-3 rounded-xl border text-sm font-semibold disabled:opacity-40 ${collectionTime === "asap" ? "border-metros-red bg-red-50" : "border-gray-200"}`}>As soon as possible</button>
                    <button data-testid="dl-time-later" onClick={() => setCollectionTime(collectionTime === "asap" ? (delivW.open || "12:00") : collectionTime)} className={`py-3 rounded-xl border text-sm font-semibold ${collectionTime !== "asap" ? "border-metros-red bg-red-50" : "border-gray-200"}`}>Schedule for later</button>
                  </div>
                  {collectionTime !== "asap" && <input type="time" data-testid="delivery-time" value={collectionTime} onChange={(e) => setCollectionTime(e.target.value)} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />}
                  {!delivW.openNow && <p className="text-xs text-metros-red" data-testid="delivery-closed-note">Delivery starts at {delivW.open || "later"} today — order for collection now, or schedule delivery for later.</p>}
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-xl p-4 text-sm">
                  <p className="font-semibold flex items-center gap-2"><Store size={16} /> {settings?.collection?.location}</p>
                  <p className="text-muted-foreground mt-1">Prep time: {settings?.collection?.prep_time}. {settings?.collection?.instructions}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid="co-time-asap" disabled={!collW.openNow} onClick={() => setCollectionTime("asap")} className={`py-3 rounded-xl border text-sm font-semibold disabled:opacity-40 ${collectionTime === "asap" ? "border-metros-red bg-red-50" : "border-gray-200"}`}>As soon as possible</button>
                  <button data-testid="co-time-later" onClick={() => setCollectionTime(collectionTime === "asap" ? (collW.open || "12:00") : collectionTime)} className={`py-3 rounded-xl border text-sm font-semibold ${collectionTime !== "asap" ? "border-metros-red bg-red-50" : "border-gray-200"}`}>Schedule for later</button>
                </div>
                {collectionTime !== "asap" && <input type="time" data-testid="collection-time" value={collectionTime} onChange={(e) => setCollectionTime(e.target.value)} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />}
                <p className="text-xs text-muted-foreground">Collection today: {windowLabel(collW)}.{!collW.openNow ? " Closed now — please schedule for later." : ""}</p>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="step-payment">
            <h2 className="font-heading font-bold text-xl">Payment</h2>
            {pm.card && (
              <button onClick={() => setPaymentMethod("card")} data-testid="pay-card"
                className={`w-full flex items-center gap-3 border-2 rounded-xl p-4 text-left transition-colors ${paymentMethod === "card" ? "border-metros-red bg-red-50" : "border-gray-200"}`}>
                <CreditCard className={paymentMethod === "card" ? "text-metros-red" : "text-gray-500"} />
                <div><p className="font-semibold">Card / Apple Pay / Google Pay</p><p className="text-xs text-muted-foreground">Pay securely online via Stripe.</p></div>
                {paymentMethod === "card" && <Check className="ml-auto text-metros-red" />}
              </button>
            )}
            {pm.cash && (
              <button onClick={() => setPaymentMethod("cash")} data-testid="pay-cash"
                className={`w-full flex items-center gap-3 border-2 rounded-xl p-4 text-left transition-colors ${paymentMethod === "cash" ? "border-metros-red bg-red-50" : "border-gray-200"}`}>
                <Banknote className={paymentMethod === "cash" ? "text-metros-red" : "text-gray-500"} />
                <div><p className="font-semibold">Cash on {orderType === "delivery" ? "delivery" : "collection"}</p><p className="text-xs text-muted-foreground">Pay when your order arrives.</p></div>
                {paymentMethod === "cash" && <Check className="ml-auto text-metros-red" />}
              </button>
            )}
            {pm.card && <p className="text-xs text-muted-foreground">Test card: 4242 4242 4242 4242, any future expiry, any CVC.</p>}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="step-review">
            <h2 className="font-heading font-bold text-xl">Review your order</h2>
            <div className="space-y-2">
              {items.map((it) => {
                const addons = it.meal_addons || [];
                const optNames = (it.selected_options || []).map((o) => o.name);
                const linePrice = (it.price + (it.selected_options || []).reduce((s, o) => s + o.price, 0) + addons.reduce((s, a) => s + (a.price || 0), 0)) * it.quantity;
                return (
                <div key={it.uid} className="text-sm">
                  <div className="flex justify-between items-center gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      {it.image ? (
                        <img src={it.image} alt={it.name} className="h-10 w-10 rounded-lg object-cover shrink-0" />
                      ) : (
                        <div className="h-10 w-10 rounded-lg bg-gray-50 flex items-center justify-center shrink-0"><img src="/logo.png" alt="METROS" className="h-6 w-6 object-contain opacity-60" /></div>
                      )}
                      <span className="truncate">{it.quantity}× {it.name}{optNames.length ? ` (${optNames.join(", ")})` : ""}</span>
                    </div>
                    <span className="font-semibold shrink-0">{money(linePrice)}</span>
                  </div>
                  {addons.map((a, idx) => (
                    <div key={idx} className="flex justify-between text-xs text-muted-foreground pl-4">
                      <span>+ {a.name}</span><span>{money(a.price || 0)}</span>
                    </div>
                  ))}
                </div>
                );
              })}
            </div>
            {rwEligible && rwMax > 0 && (
              <div className="border border-metros-red/20 rounded-xl p-4 bg-red-50/50" data-testid="redeem-points">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm flex items-center gap-1.5"><Gift size={15} className="text-metros-red" /> Reward points</p>
                    <p className="text-xs text-muted-foreground">Balance: {rwBalance} pts · {rwThreshold} pts = {money(rwValue)} off</p>
                  </div>
                  {redeemPoints > 0 && (
                    <button data-testid="redeem-clear" onClick={() => setRedeemPoints(0)} className="text-xs font-semibold text-metros-red underline">Remove</button>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <button data-testid="redeem-minus" onClick={() => setRedeemPoints(Math.max(0, redeemPoints - rwThreshold))} disabled={redeemPoints <= 0}
                    className="h-9 w-9 rounded-full border border-gray-200 bg-white font-bold text-lg leading-none disabled:opacity-40 active:scale-95 transition-all">−</button>
                  <div className="flex-1 text-center">
                    <p className="font-heading font-bold" data-testid="redeem-amount">{redeemPoints} pts</p>
                    <p className="text-xs text-metros-red">−{money((redeemPoints / rwThreshold) * rwValue)}</p>
                  </div>
                  <button data-testid="redeem-plus" onClick={() => setRedeemPoints(Math.min(rwEffMax, redeemPoints + rwThreshold))} disabled={redeemPoints >= rwEffMax}
                    className="h-9 w-9 rounded-full border border-gray-200 bg-white font-bold text-lg leading-none disabled:opacity-40 active:scale-95 transition-all">+</button>
                </div>
                {rwEffMax > 0 && redeemPoints < rwEffMax && (
                  <button data-testid="redeem-max" onClick={() => setRedeemPoints(rwEffMax)} className="w-full mt-2 text-xs font-semibold text-metros-red">Use maximum ({rwEffMax} pts · −{money((rwEffMax / rwThreshold) * rwValue)})</button>
                )}
                {paymentMethod === "card" && rwEffMax < rwMax && (
                  <p data-testid="redeem-card-note" className="mt-2 text-xs text-muted-foreground">
                    {rwEffMax === 0
                      ? "Card payments have a £0.30 minimum, so points can't be applied to this order. Pay by cash to redeem your points."
                      : "Points are limited on card orders to keep your total above the £0.30 minimum. Pay by cash to redeem more."}
                  </p>
                )}
              </div>
            )}
            <div className="border border-gray-100 rounded-xl p-3" data-testid="checkout-promo">
              {promoCode ? (
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-metros-red flex items-center gap-1.5"><Gift size={14} /> Promo “{promoCode}” applied</span>
                  <button data-testid="promo-remove" onClick={() => { setPromoCode(""); setPromoInput(""); }} className="text-xs font-semibold text-metros-red underline">Remove</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input data-testid="promo-input" value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} placeholder="Promo code" className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm uppercase focus:ring-2 focus:ring-metros-red focus:outline-none" />
                  <button data-testid="apply-promo" onClick={() => { if (promoInput.trim()) setPromoCode(promoInput.trim()); }} className="bg-metros-black text-white rounded-lg px-4 text-sm font-semibold">Apply</button>
                </div>
              )}
              {promoCode && totals?.promo_error && <p data-testid="promo-invalid" className="text-xs text-metros-red mt-1.5">{totals.promo_error}</p>}
              {promoCode && totals && !totals.promo_error && totals.discount > 0 && <p className="text-xs text-green-600 mt-1.5">Discount applied 🎉</p>}
            </div>
            {totals && (
              <div className="border-t border-gray-100 pt-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{money(totals.subtotal)}</span></div>
                {orderType === "delivery" && <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span>{totals.delivery_fee === 0 ? "FREE" : money(totals.delivery_fee)}</span></div>}
                {totals.discount > 0 && <div className="flex justify-between text-metros-red"><span>Discount {promoCode ? `(${promoCode})` : ""}</span><span>-{money(totals.discount)}</span></div>}
                {totals.combo_discount > 0 && <div className="flex justify-between text-metros-red" data-testid="checkout-meal-saving"><span>Meal deal saving</span><span>-{money(totals.combo_discount)}</span></div>}
                {totals.reward_discount > 0 && <div className="flex justify-between text-green-600 font-semibold" data-testid="checkout-reward-discount"><span>Loyalty reward ({totals.order_reward_percent}% off)</span><span>-{money(totals.reward_discount)}</span></div>}
                {totals.reward_items_discount > 0 && <div className="flex justify-between text-green-600 font-semibold" data-testid="checkout-reward-items-discount"><span>Reward redeemed</span><span>-{money(totals.reward_items_discount)}</span></div>}
                {totals.points_discount > 0 && <div className="flex justify-between text-metros-red"><span>Points ({totals.points_redeemed})</span><span>-{money(totals.points_discount)}</span></div>}
                <div className="flex justify-between font-heading font-extrabold text-lg pt-1"><span>Total</span><span>{money(totals.total)}</span></div>
                {totals.points_earned > 0 && <p className="text-xs text-metros-red">You'll earn {totals.points_earned} reward points 🎁</p>}
                {user && totals.order_reward_enabled && totals.reward_order && (
                  <p data-testid="reward-applied" className="text-xs font-bold text-green-600">🎉 {totals.order_reward_percent}% off applied — your loyalty reward is here!</p>
                )}
              </div>
            )}
            <div className="text-xs text-muted-foreground bg-gray-50 rounded-xl p-3">
              {orderType === "delivery" ? `Delivering to ${address.line1}, ${address.postcode}` : `Collection from ${settings?.collection?.location}`} · {paymentMethod === "card" ? "Paying by card" : "Cash payment"}
            </div>
          </div>
        )}
      </motion.div>

      {step === 3 && totals && totals.order_reward_enabled && totals.order_reward_percent > 0 && !totals.reward_order && (
        user
          ? (totals.orders_until_reward != null && totals.orders_until_reward > 0 && (
              <div data-testid="reward-progress" className="mt-4 rounded-xl bg-metros-red/10 border border-metros-red/30 px-4 py-3 text-center">
                <p className="font-heading font-extrabold text-metros-red text-base">Order {totals.orders_until_reward} more time{totals.orders_until_reward === 1 ? "" : "s"} to get {totals.order_reward_percent}% off 🎁</p>
              </div>
            ))
          : (
              <div data-testid="reward-signin-nudge" className="mt-4 rounded-xl bg-metros-red/10 border border-metros-red/30 px-4 py-3 text-center">
                <p className="font-heading font-extrabold text-metros-red text-base">Sign in to earn {totals.order_reward_percent}% off your {totals.order_reward_every}th order 🎁</p>
              </div>
            )
      )}

      {step === 3 && user && totals && totals.order_reward_enabled && totals.reward_order && totals.reward_discount > 0 && (
        <div data-testid="reward-applied-banner" className="mt-4 rounded-xl bg-green-50 border border-green-300 px-4 py-3 text-center">
          <p className="font-heading font-extrabold text-green-700 text-base">🎉 {totals.order_reward_percent}% loyalty reward applied — you saved {money(totals.reward_discount)} on this order!</p>
        </div>
      )}

      {step < 3 ? (
        <div className="flex flex-wrap gap-3 mt-5">
          {step > 0 && <button data-testid="co-back" onClick={back} className="px-6 py-3.5 rounded-full border border-gray-200 font-semibold">Back</button>}
          <button data-testid="co-next" onClick={next} className="flex-1 bg-metros-black hover:bg-black text-white rounded-full py-3.5 font-bold transition-colors">Continue</button>
        </div>
      ) : (
        <div className="mt-5">
          <button data-testid="place-order" onClick={placeOrder} disabled={busy || !totals || isClosedNow || outsideDelivery} className="w-full bg-metros-red hover:bg-metros-redhover text-white rounded-full py-3.5 font-bold flex justify-center items-center gap-2 disabled:opacity-50 active:scale-95 transition-all">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <ShoppingBag size={18} />} {isClosedNow ? "Branch closed" : (paymentMethod === "card" ? "Pay & Place Order" : "Place Order")} {totals && !isClosedNow ? `· ${money(totals.total)}` : ""}
          </button>
          <div className="flex justify-between items-center mt-2 px-1">
            <button data-testid="co-back" onClick={back} className="text-xs text-gray-500 hover:text-metros-black underline px-2 py-1">Back</button>
            <button ref={branchBtnRef} data-testid="co-select-branch" onClick={() => setBranchGuard(true)}
              className={`text-xs font-semibold px-2.5 py-1 inline-flex items-center gap-1 rounded-md transition-all ${selectedBranch ? "text-metros-red hover:underline" : "text-amber-800 bg-amber-100 animate-pulse"}`}>
              <MapPin size={12} className="shrink-0" /> {selectedBranch ? selectedBranch.name.replace("METROS — ", "") : "Find branch"}
            </button>
          </div>
        </div>
      )}
      {step === 3 && outsideDelivery && (
        <div data-testid="place-order-blocked" className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm">
          <p className="text-amber-900 flex items-start gap-2">
            <MapPin size={16} className="text-amber-600 shrink-0 mt-0.5" />
            <span>
              <b>{address.postcode}</b>{pcStatus?.distance != null ? ` is ${pcStatus.distance.toFixed(1)} miles away —` : " looks"} outside {selectedBranch?.name?.replace("METROS — ", "") || "this branch"}'s {settings?.delivery?.radius_miles}-mile delivery area.
              {outsideInfo?.other
                ? <> Good news — <b>{outsideInfo.other.name.replace("METROS — ", "")}</b> can deliver to you{outsideInfo.other.distance != null ? ` (${outsideInfo.other.distance.toFixed(1)} miles away)` : ""}.</>
                : <> You can still order for <b>collection</b>.</>}
            </span>
          </p>
          <div className="mt-2 ml-6 flex flex-wrap gap-4">
            {outsideInfo?.other && (
              <button data-testid="switch-branch" onClick={async () => {
                const nb = outsideInfo.other;
                setSelectedBranch({ id: nb.id, name: nb.name });
                toast.success(`Switched to ${nb.name.replace("METROS — ", "")}`);
                try { const { data } = await api.get(`/delivery/check?postcode=${encodeURIComponent(address.postcode)}&branch_id=${encodeURIComponent(nb.id)}`); setPcStatus(data); }
                catch { setPcStatus(null); }
              }} className="text-metros-red font-bold underline">Switch to {outsideInfo.other.name.replace("METROS — ", "")}</button>
            )}
            <button data-testid="switch-to-collection" onClick={() => setOrderType("collection")} className="text-metros-red font-bold underline">Switch to collection</button>
          </div>
        </div>
      )}
      {showAuth && (
        <div data-testid="checkout-auth-modal" className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4" onClick={() => !authBusy && setShowAuth(false)}>
          <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading font-extrabold text-xl">Almost there!</h3>
                <p className="text-sm text-muted-foreground mt-0.5">Sign in to save your order & earn rewards — or just continue as a guest.</p>
              </div>
              <button data-testid="auth-modal-close" onClick={() => setShowAuth(false)} className="text-gray-400 hover:text-gray-600 shrink-0"><X size={20} /></button>
            </div>
            {authErr && <div className="bg-red-50 text-metros-red text-sm rounded-xl px-4 py-2.5 mt-4">{authErr}</div>}

            {authView === "choices" && (
              <div className="space-y-2.5 mt-5">
                <button data-testid="auth-guest" onClick={submitOrder} disabled={authBusy} className="w-full bg-metros-red hover:bg-metros-redhover text-white rounded-full py-3.5 font-bold flex justify-center items-center gap-2 disabled:opacity-60 active:scale-95 transition-all">{authBusy ? <Loader2 className="animate-spin" size={16} /> : <ShoppingBag size={16} />} Continue as guest</button>
                <button data-testid="auth-google" onClick={loginWithGoogleCheckout} className="w-full border border-gray-200 hover:bg-gray-50 rounded-full py-3.5 font-bold flex justify-center items-center gap-3 active:scale-95 transition-all"><img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" /> Continue with Google</button>
                <div className="grid grid-cols-2 gap-2.5">
                  <button data-testid="auth-signin" onClick={() => { setAuthErr(""); setAuthView("signin"); }} className="border border-gray-200 hover:border-metros-red rounded-full py-3 font-bold text-sm">Sign in</button>
                  <button data-testid="auth-create" onClick={() => { setAuthErr(""); setAuthView("create"); }} className="border border-gray-200 hover:border-metros-red rounded-full py-3 font-bold text-sm">Create account</button>
                </div>
                <p className="text-[11px] text-center text-muted-foreground pt-1">You're never forced to sign in — guests can order in seconds.</p>
              </div>
            )}

            {authView === "signin" && (
              <div className="space-y-3 mt-5" data-testid="auth-signin-form">
                <input data-testid="ci-email" type="email" placeholder="Email" value={authForm.email} onChange={setAF("email")} className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:ring-2 focus:ring-metros-red focus:outline-none" />
                <input data-testid="ci-password" type="password" placeholder="Password" value={authForm.password} onChange={setAF("password")} className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:ring-2 focus:ring-metros-red focus:outline-none" />
                <button data-testid="ci-signin-btn" onClick={doSignIn} disabled={authBusy} className="w-full bg-metros-red text-white rounded-full py-3 font-bold flex justify-center items-center gap-2 disabled:opacity-60">{authBusy && <Loader2 className="animate-spin" size={16} />} Sign in &amp; place order</button>
                <button onClick={() => { setAuthErr(""); setAuthView("choices"); }} className="w-full text-sm text-muted-foreground flex items-center justify-center gap-1"><ChevronLeft size={14} /> Back</button>
              </div>
            )}

            {authView === "create" && (
              <div className="space-y-3 mt-5" data-testid="auth-create-form">
                <input data-testid="cc-name" placeholder="Full name" value={authForm.name} onChange={setAF("name")} className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:ring-2 focus:ring-metros-red focus:outline-none" />
                <input data-testid="cc-email" type="email" placeholder="Email" value={authForm.email} onChange={setAF("email")} className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:ring-2 focus:ring-metros-red focus:outline-none" />
                <input data-testid="cc-phone" placeholder="Phone" value={authForm.phone} onChange={setAF("phone")} className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:ring-2 focus:ring-metros-red focus:outline-none" />
                <input data-testid="cc-password" type="password" placeholder="Password (min 6 chars)" value={authForm.password} onChange={setAF("password")} className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:ring-2 focus:ring-metros-red focus:outline-none" />
                <button data-testid="cc-create-btn" onClick={doCreate} disabled={authBusy} className="w-full bg-metros-red text-white rounded-full py-3 font-bold flex justify-center items-center gap-2 disabled:opacity-60">{authBusy && <Loader2 className="animate-spin" size={16} />} Create account &amp; place order</button>
                <button onClick={() => { setAuthErr(""); setAuthView("choices"); }} className="w-full text-sm text-muted-foreground flex items-center justify-center gap-1"><ChevronLeft size={14} /> Back</button>
              </div>
            )}
          </div>
        </div>
      )}
      <BranchSelect open={branchGuard} onClose={() => setBranchGuard(false)} forced={!selectedBranch} />
    </div>
  );
}
